import {
  inject,
  Injectable,
  Injector,
  reflectComponentType,
  SecurityContext,
  signal,
  WritableSignal,
} from '@angular/core';
import { createCustomElement } from '@angular/elements';
import { DomSanitizer } from '@angular/platform-browser';

import { MastraClient } from '@mastra/client-js';
import { marked } from 'marked';

import { APP_CONFIG, AUTH_METHOD, AUTH_TOKEN, localStorageSignal } from '@core/common-legacy';
import { TellentCurrentUserDataFacade } from '@core/tellent-logic';
import { ORG_GUID, type TellentAdminAppConfig } from '@core/tellent-types';
import { generateUuid } from '@core/utils-legacy';

import { TellentChatComponent } from './tellent-chat.component';

export interface AiMessage {
  id: string;
  role: 'user' | 'assistant' | 'system' | 'data' | 'signal';
  content: string;
  createdAt?: Date;
  toolInvocations?: unknown[];
  isPending?: boolean;
  isError?: boolean;
  errorMessage?: string | null;
  isComponent?: boolean;
}

@Injectable({ providedIn: 'root' })
export class HrAssistantChatService {
  private readonly token = inject(AUTH_TOKEN);
  private readonly appConfig = inject<TellentAdminAppConfig>(APP_CONFIG);
  private readonly method = inject(AUTH_METHOD);
  private readonly companyGuid = inject(ORG_GUID);
  private readonly domSanitizer = inject(DomSanitizer);
  private readonly agentId = 'hrAssistant';
  private readonly mastraClient = new MastraClient({
    baseUrl: `${this.appConfig.tellentApiUrl}/intelligence/org/${this.companyGuid}/assistant/proxy`,
    headers: {
      Authorization: `${this.method} ${this.token}`,
    },
  });

  private readonly currentUserGuid = inject(TellentCurrentUserDataFacade).currentUserGuid;

  public isNewThread = localStorageSignal('hr-assistant-new-thread', false);
  public threads = signal<Record<string, WritableSignal<Array<WritableSignal<AiMessage>>>>>({});
  public currentThreadId = signal('');

  private hrAssistant = this.mastraClient.getAgent(this.agentId);

  constructor() {
    this.registerChatCustomElement(inject(Injector));
  }

  public async loadThreads(restoreLastThread: boolean) {
    const { threads } = await this.mastraClient.listMemoryThreads({
      agentId: this.agentId,
      resourceId: this.currentUserGuid() || '',
    });
    if (threads.length === 0 || !restoreLastThread) {
      this.startNewThread();
    } else if (restoreLastThread) {
      await this.restoreThread(threads[threads.length - 1].id);
    }
  }

  public async sendMessage(value: string) {
    this.isNewThread.set(false);
    const threadId = this.currentThreadId();
    const messages = this.threads()[threadId];
    const userMessage = signal(<AiMessage>{
      id: generateUuid(),
      role: 'user',
      content: value,
    });
    const assistantPlaceholderMsg = signal(<AiMessage>{
      id: generateUuid(),
      role: 'assistant',
      content: '',
      isPending: true,
    });
    messages.update(v => [...v, userMessage, assistantPlaceholderMsg]);
    try {
      await this.hrAssistant.generate([{ role: 'user', content: value }], {
        memory: {
          thread: threadId,
          resource: this.currentUserGuid()!,
        },
      });
      await this.restoreThread(this.currentThreadId());
    } catch (error) {
      const parsedError = this.tryParseError(error);
      userMessage.update(msg => ({
        ...msg,
        isError: true,
        errorMessage: parsedError,
        id: generateUuid(),
      }));
      messages.update(messages =>
        messages.filter(msg => msg().id !== assistantPlaceholderMsg().id),
      );
    }
  }

  public async retry(message: AiMessage) {
    const threadId = this.currentThreadId();
    const messages = this.threads()[threadId];
    const isRetryCurrentThread = messages().some(msg => msg().id === message.id);
    if (!isRetryCurrentThread) return;

    await this.restoreThread(threadId);
    this.sendMessage(message.content);
  }

  public startNewThread() {
    const uuid = generateUuid();
    this.currentThreadId.set(uuid);
    this.threads.update(threads => ({
      ...threads,
      [uuid]: signal([]),
    }));
  }

  public registerChatCustomElement(injector: Injector) {
    const mirror = reflectComponentType(TellentChatComponent)!;
    const isDefined = customElements.get(mirror.selector);
    if (isDefined) return;

    customElements.define(mirror.selector, createCustomElement(TellentChatComponent, { injector }));
  }

  private async restoreThread(threadId: string) {
    this.currentThreadId.set(threadId);
    const thread = this.mastraClient.getMemoryThread({ threadId, agentId: this.agentId });
    const response = await thread.listMessages();
    const messagesGrouped = response.messages.reduce(
      (acc, message) => {
        const prevMessage = acc[acc.length - 1];
        const textContent = (<{ content?: string }>message.content).content ?? '';
        const toolInvocations =
          (<{ toolInvocations?: unknown[] }>message.content).toolInvocations ?? [];
        const isComponent = textContent.includes('<component>');
        if (!prevMessage || prevMessage.role !== message.role) {
          return [
            ...acc,
            {
              id: message.id,
              role: message.role,
              createdAt: message.createdAt,
              content: this.processMessageContent(textContent),
              isComponent,
              toolInvocations,
            },
          ];
        }

        // Merge messages from the same role
        const content = this.processMessageContent(
          prevMessage.content ? `${prevMessage.content}\n${textContent}` : textContent,
        );

        return Object.assign([], acc, {
          [acc.length - 1]: {
            ...prevMessage,
            content,
            isComponent,
            toolInvocations: [...(prevMessage.toolInvocations || []), ...toolInvocations],
          },
        });
      },
      <AiMessage[]>[],
    );
    this.threads.update(threads => ({
      ...threads,
      [threadId]: signal(messagesGrouped.map(m => signal(m))),
    }));
  }

  private processMessageContent(message: string) {
    const markComponents = message
      .replaceAll('<component>', '[component]')
      .replaceAll('</component>', '[/component]');
    const markdownToHtml = marked(markComponents, { async: false });
    const sanitizedContent = this.domSanitizer.sanitize(SecurityContext.HTML, markdownToHtml) ?? '';
    const final = sanitizedContent
      .replaceAll('[component]', '<tellent-chat-component>')
      .replaceAll('[/component]', '</tellent-chat-component>');

    return final;
  }

  private tryParseError(err: Error): string | null {
    try {
      const [_message, error] = err.message.split(' - ');
      const parsedError: { errors: string[] } = JSON.parse(error);
      return parsedError.errors[0] ?? null;
    } catch {
      return null;
    }
  }
}
